import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import TrashIcon from "@/assets/icons/trash.svg?react";
import {
  volumesOptions,
  volumesOptionsWoodBox,
  VolumesWoodBoxAvailable,
} from "@/constants/options/volumes.option";
import StyledInput from "@/components/styled/StyledInput";
import { StyledSelectSingleValueInt } from "@/components/styled/StyledSelectSingleValueInt";
import { DeliveryFormData } from "@/types/forms/home/cotationDeliveryForm.form";
import { Button } from "@/components/ui/button";

export const InfoPackagingBo = () => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<DeliveryFormData>();

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "woodBoxes",
  });

  const handleAppendWoodbox = async () => {
    append({
      bottleQuantity: "" as unknown as number,
      boxQuantity: "" as unknown as number,
      bottleVolume: volumesOptions[0].value,
    });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="flex flex-row items-center gap-2 self-start">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">3</p>
          </div>
          <p className="text-xl font-bold">{t("infoPackaging.title")}</p>
        </div>
        <div className="flex w-full flex-col gap-2">
          {fields.map((field, index) => (
            <WoodboxItem key={field.id} index={index} remove={remove} />
          ))}
          <Button
            variant="outline"
            onClick={handleAppendWoodbox}
            className="self-end!"
            type="button"
          >
            {t("infoPackaging.addWoodBox")}
          </Button>
        </div>
      </div>
    </div>
  );
};

type WoodboxItemProps = {
  index: number;
  remove: (index: number) => void;
};

const WoodboxItem = ({ index, remove }: WoodboxItemProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<DeliveryFormData>();

  const selectedVolume = useWatch({
    control: methods.control,
    name: `woodBoxes.${index}.bottleVolume`,
  });

  const woodBoxWatch = useWatch({
    control: methods.control,
    name: `woodBoxes.${index}`,
  });

  const getBottleQuantityOptions = (volume: number) => {
    const volumeData = VolumesWoodBoxAvailable.find((v) => v.value === volume);
    if (!volumeData) return [];
    const available = volumeData.available;
    if (!available.includes(woodBoxWatch.bottleQuantity)) {
      methods.resetField(`woodBoxes.${index}.bottleQuantity`);
    }
    return available
      .sort((a, b) => a - b)
      .map((quantity) => ({
        label: quantity.toString(),
        value: quantity,
      }));
  };

  const bottleQuantityOptions = useMemo(
    () => getBottleQuantityOptions(selectedVolume),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedVolume],
  );

  return (
    <div
      key={`${index}`}
      className="flex flex-1 flex-col items-start gap-2 rounded-lg border-2 border-gray-300 bg-gray-50 p-4 md:flex-row"
    >
      <div className="flex w-full flex-1 flex-col gap-2 sm:flex-row">
        <StyledSelectSingleValueInt
          title={t("infoPackaging.volumeLabel")}
          setValue={(e: any) => {
            methods.setValue(`woodBoxes.${index}.bottleVolume`, e);
          }}
          options={volumesOptionsWoodBox}
          control={methods.control}
          name={`woodBoxes.${index}.bottleVolume`}
          className="w-full"
          isSearchable={false}
        />
        <StyledSelectSingleValueInt
          key={`${index}-${JSON.stringify(bottleQuantityOptions)}`}
          options={bottleQuantityOptions}
          setValue={(e: any) => {
            methods.setValue(`woodBoxes.${index}.bottleQuantity`, e);
            methods.trigger([`woodBoxes.${index}.bottleQuantity`]);
          }}
          title={t("infoPackaging.bottleQuantityLabel")}
          control={methods.control}
          name={`woodBoxes.${index}.bottleQuantity`}
          className="w-full"
          isSearchable={false}
        />
      </div>
      <div className="flex w-full flex-1 flex-row gap-2">
        <StyledInput
          key={woodBoxWatch.bottleQuantity}
          title={t("infoPackaging.boxQuantityLabel")}
          control={methods.control}
          name={`woodBoxes.${index}.boxQuantity`}
          type="number"
          className="w-full"
        />
        <div className="flex flex-1 flex-row items-center gap-2">
          <button
            type="button"
            onClick={() => remove(index)}
            className="flex h-fit items-center justify-center rounded-full p-2 hover:bg-gray-200"
          >
            <TrashIcon className="text-red-600" fill="red" width={20} height={20} />
          </button>
        </div>
      </div>
    </div>
  );
};
